import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { getReviewDetail, approveReview, rejectReview, IMAGE_BASE_URL } from '../../services/api';
import {
  FiArrowLeft,
  FiStar,
  FiCheckCircle,
  FiXCircle,
  FiUser,
  FiPackage,
  FiShoppingBag,
  FiMessageSquare,
  FiTruck,
  FiAward,
  FiAlertCircle,
  FiEdit,
  FiClock,
  FiMail,
  FiTag,
  FiInfo,
  FiRefreshCw,
  FiTrendingUp,
  FiTrendingDown,
  FiX
} from 'react-icons/fi';

function ReviewDetail({ admin }) {
  const { reviewId } = useParams();
  const navigate = useNavigate();
  const [review, setReview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectionReason, setRejectionReason] = useState('Violation of review guidelines');
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  const fetchReviewDetail = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await getReviewDetail(reviewId);
      
      if (response.success && response.data) {
        setReview(response.data.review);
      } else {
        setReview(response.review);
      }
    } catch (error) {
      setError('Failed to load review details: ' + error.message);
      console.error('Error fetching review details:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReviewDetail();
  }, [reviewId]);

  const handleApprove = async () => {
    if (!window.confirm('Are you sure you want to approve this review?')) return;
    
    try {
      setActionLoading(true);
      setError('');
      await approveReview(reviewId);
      setSuccessMessage('Review approved successfully!');
      setTimeout(() => setSuccessMessage(''), 3000);
      fetchReviewDetail();
    } catch (error) {
      setError('Failed to approve review: ' + error.message);
    } finally {
      setActionLoading(false);
    }
  };

  const handleReject = async () => {
    if (!rejectionReason.trim()) {
      setError('Please provide a rejection reason.');
      return;
    }

    try {
      setActionLoading(true);
      setError('');
      await rejectReview(reviewId, rejectionReason);
      setSuccessMessage('Review rejected successfully!');
      setTimeout(() => setSuccessMessage(''), 3000);
      setShowRejectModal(false);
      fetchReviewDetail();
    } catch (error) {
      setError('Failed to reject review: ' + error.message);
    } finally {
      setActionLoading(false);
    }
  };

  const renderStars = (rating, label) => {
    if (!rating) return null;
    
    const ratingConfig = getRatingConfig(rating);
    
    return (
      <div className="flex items-center gap-3">
        <div className="flex space-x-0.5">
          {Array.from({ length: 5 }).map((_, i) => (
            <FiStar
              key={i}
              className={`w-4 h-4 ${
                i < rating ? 'fill-yellow-400 text-yellow-400' : 'text-gray-600'
              }`}
            />
          ))}
        </div>
        <div className={`inline-flex items-center px-2.5 py-1 rounded-lg text-xs font-medium ${ratingConfig.bg} ${ratingConfig.color}`}>
          {ratingConfig.icon}
          <span className="ml-1 font-bold">{rating}/5</span>
        </div>
        {label && <span className="text-sm text-gray-400 ml-2">{label}</span>}
      </div>
    );
  };

  const getStatusConfig = (status) => {
    const configs = {
      pending: {
        color: 'text-yellow-400',
        bg: 'bg-yellow-900/30',
        border: 'border-yellow-800/50',
        icon: <FiClock className="w-4 h-4" />,
        label: 'Pending'
      },
      approved: {
        color: 'text-green-400',
        bg: 'bg-green-900/30',
        border: 'border-green-800/50',
        icon: <FiCheckCircle className="w-4 h-4" />,
        label: 'Approved'
      },
      rejected: {
        color: 'text-red-400',
        bg: 'bg-red-900/30',
        border: 'border-red-800/50',
        icon: <FiXCircle className="w-4 h-4" />,
        label: 'Rejected'
      }
    };
    return configs[status] || configs.pending;
  };

  const getRatingConfig = (rating) => {
    if (rating >= 4) return { color: 'text-green-400', bg: 'bg-green-900/30', icon: <FiTrendingUp /> };
    if (rating >= 3) return { color: 'text-yellow-400', bg: 'bg-yellow-900/30', icon: <FiStar /> };
    if (rating >= 2) return { color: 'text-orange-400', bg: 'bg-orange-900/30', icon: <FiTrendingDown /> };
    return { color: 'text-red-400', bg: 'bg-red-900/30', icon: <FiAlertCircle /> };
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (e) {
      return dateString;
    }
  };

  const formatCurrency = (amount) => {
    if (!amount) return '₦0.00';
    return `₦${parseFloat(amount).toFixed(2).replace(/\d(?=(\d{3})+\.)/g, '$&,')}`;
  };

  // Loading State
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-900 p-6">
        <div className="animate-pulse">
          <div className="mb-6">
            <div className="h-4 bg-gray-700 rounded w-32 mb-2"></div>
            <div className="h-8 bg-gray-700 rounded w-64"></div>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2 space-y-6">
              {[...Array(2)].map((_, i) => (
                <div key={i} className="bg-gray-800 rounded-xl border border-gray-700 p-6">
                  <div className="h-6 bg-gray-700 rounded w-48 mb-4"></div>
                  <div className="space-y-4">
                    {[...Array(3)].map((_, j) => (
                      <div key={j} className="h-4 bg-gray-700 rounded w-full"></div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
            <div className="space-y-6">
              {[...Array(3)].map((_, i) => (
                <div key={i} className="bg-gray-800 rounded-xl border border-gray-700 p-6">
                  <div className="h-6 bg-gray-700 rounded w-32 mb-4"></div>
                  <div className="space-y-3">
                    {[...Array(2)].map((_, j) => (
                      <div key={j} className="h-4 bg-gray-700 rounded w-full"></div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!review) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center p-6">
        <div className="text-center max-w-md">
          <div className="text-gray-600 text-6xl mb-6">🔍</div>
          <h3 className="text-xl font-medium text-white mb-2">Review not found</h3>
          <p className="text-gray-400 mb-6">The review you're looking for doesn't exist or has been removed.</p>
          <button
            onClick={() => navigate('/admin/reviews')}
            className="px-6 py-2.5 bg-gradient-to-r from-[#ff660e] to-orange-500 text-white rounded-lg hover:from-[#ff5500] hover:to-orange-600 transition-colors font-medium inline-flex items-center hover:scale-[1.02]"
          >
            <FiArrowLeft className="mr-2" />
            Back to Reviews
          </button>
        </div>
      </div>
    );
  }

  const statusConfig = getStatusConfig(review.status);

  return (
    <div className="min-h-screen bg-gray-900 p-4 md:p-6">
      {/* Success Message */}
      {successMessage && (
        <div className="mb-6 bg-green-900/20 border border-green-800/50 rounded-xl p-4 flex items-center justify-between animate-fade-in backdrop-blur-sm">
          <div className="flex items-center space-x-3">
            <FiCheckCircle className="text-green-400 text-xl" />
            <p className="text-green-300 font-medium">{successMessage}</p>
          </div>
          <button 
            onClick={() => setSuccessMessage('')}
            className="text-green-400 hover:text-green-300 text-xl"
          >
            ×
          </button>
        </div>
      )}

      {/* Error Alert */}
      {error && (
        <div className="mb-6 bg-red-900/20 border border-red-800/50 rounded-xl p-4 flex items-center justify-between animate-fade-in backdrop-blur-sm">
          <div className="flex items-center space-x-3">
            <FiAlertCircle className="text-red-400 text-xl" />
            <p className="text-red-300">{error}</p>
          </div>
          <button 
            onClick={() => setError('')}
            className="text-red-400 hover:text-red-300 text-xl"
          >
            ×
          </button>
        </div>
      )}

      {/* Header */}
      <div className="mb-6">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div className="flex items-center space-x-4">
            <Link
              to="/admin/reviews"
              className="p-2 bg-gray-800 border border-gray-700 rounded-lg hover:bg-gray-700 hover:border-gray-600 transition-colors"
            >
              <FiArrowLeft className="w-5 h-5 text-gray-400" />
            </Link>
            <div>
              <h1 className="text-2xl md:text-3xl font-bold text-white">
                <span className="text-[#ff660e]">Review</span> Details
              </h1>
              <p className="text-gray-400 mt-1">Review #{review.rating_id} • {formatDate(review.created_at)}</p>
            </div>
          </div>
          
          <div className="flex items-center space-x-3">
            <span className={`inline-flex items-center px-4 py-2 rounded-full text-sm font-medium ${statusConfig.bg} ${statusConfig.color} ${statusConfig.border}`}>
              {statusConfig.icon}
              <span className="ml-2">{statusConfig.label}</span>
            </span>
            
            <button
              onClick={() => fetchReviewDetail()}
              disabled={loading}
              className="p-2 bg-gray-800 text-white border border-gray-700 rounded-lg hover:bg-gray-700 hover:border-blue-500 transition-all duration-200 disabled:opacity-50"
            >
              <FiRefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
            </button>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Column - Review Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Review Card */}
          <div className="bg-gray-800 rounded-xl border border-gray-700 p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-lg font-bold text-white">Review Content</h2>
              {review.is_edited && (
                <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-blue-900/30 text-blue-400 border border-blue-800/50">
                  <FiEdit className="w-3 h-3 mr-1" />
                  Edited
                </span>
              )}
            </div>

            {/* Ratings Section */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              <div className="space-y-4">
                <div className="flex items-center space-x-2">
                  <div className="w-10 h-10 rounded-lg bg-blue-900/30 flex items-center justify-center">
                    <FiAward className="w-5 h-5 text-blue-400" />
                  </div>
                  <div>
                    <h3 className="font-medium text-white">Overall Rating</h3>
                    <p className="text-sm text-gray-400">Overall experience rating</p>
                  </div>
                </div>
                {review.rating_overall && renderStars(review.rating_overall)}
                
                {review.rating_quality && (
                  <div>
                    <h4 className="text-sm font-medium text-gray-300 mb-2">Product Quality</h4>
                    {renderStars(review.rating_quality)}
                  </div>
                )}
              </div>
              
              <div className="space-y-4">
                {review.rating_communication && (
                  <div>
                    <div className="flex items-center space-x-2 mb-2">
                      <div className="w-10 h-10 rounded-lg bg-green-900/30 flex items-center justify-center">
                        <FiMessageSquare className="w-5 h-5 text-green-400" />
                      </div>
                      <div>
                        <h3 className="font-medium text-white">Communication</h3>
                        <p className="text-sm text-gray-400">Seller communication quality</p>
                      </div>
                    </div>
                    {renderStars(review.rating_communication)}
                  </div>
                )}
                
                {review.rating_delivery && (
                  <div>
                    <div className="flex items-center space-x-2 mb-2">
                      <div className="w-10 h-10 rounded-lg bg-purple-900/30 flex items-center justify-center">
                        <FiTruck className="w-5 h-5 text-purple-400" />
                      </div>
                      <div>
                        <h3 className="font-medium text-white">Delivery</h3>
                        <p className="text-sm text-gray-400">Shipping and delivery experience</p>
                      </div>
                    </div>
                    {renderStars(review.rating_delivery)}
                  </div>
                )}
              </div>
            </div>

            {/* Review Text */}
            <div className="mb-6">
              <div className="flex items-center space-x-2 mb-3">
                <div className="w-8 h-8 rounded-lg bg-gray-700 flex items-center justify-center">
                  <FiMessageSquare className="w-4 h-4 text-gray-400" />
                </div>
                <h3 className="font-medium text-white">Review Text</h3>
              </div>
              <div className="bg-gray-700/50 border border-gray-600 rounded-lg p-4">
                <p className="text-gray-300 whitespace-pre-wrap">
                  {review.review_text || 'No review text provided'}
                </p>
              </div>
            </div>

            {/* Seller Response */}
            {review.seller_response && (
              <div>
                <div className="flex items-center space-x-2 mb-3">
                  <div className="w-8 h-8 rounded-lg bg-blue-900/30 flex items-center justify-center">
                    <FiMessageSquare className="w-4 h-4 text-blue-400" />
                  </div>
                  <h3 className="font-medium text-white">Seller Response</h3>
                </div>
                <div className="bg-blue-900/20 border border-blue-800/30 rounded-lg p-4">
                  <p className="text-blue-300 whitespace-pre-wrap">
                    {review.seller_response}
                  </p>
                </div>
              </div>
            )}

            {/* Admin Notes */}
            {review.admin_notes && (
              <div className="mt-6">
                <div className="flex items-center space-x-2 mb-3">
                  <div className="w-8 h-8 rounded-lg bg-yellow-900/30 flex items-center justify-center">
                    <FiInfo className="w-4 h-4 text-yellow-400" />
                  </div>
                  <h3 className="font-medium text-white">Admin Notes</h3>
                </div>
                <div className="bg-yellow-900/20 border border-yellow-800/30 rounded-lg p-4">
                  <p className="text-yellow-300 whitespace-pre-wrap">
                    {review.admin_notes}
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* Listing Information */}
          <div className="bg-gray-800 rounded-xl border border-gray-700 p-6">
            <h2 className="text-lg font-bold text-white mb-6">Listing Information</h2>
            <div className="flex flex-col md:flex-row gap-6">
              {review.listing_image && (
                <div className="relative">
                  <img
                    src={`${IMAGE_BASE_URL}${review.listing_image}`}
                    alt={review.listing_title}
                    className="w-40 h-40 object-cover rounded-xl border border-gray-600"
                    onError={(e) => {
                      e.target.src = 'https://via.placeholder.com/160x160/374151/6b7280?text=No+Image';
                    }}
                  />
                  <div className="absolute -bottom-2 -right-2 bg-[#ff660e] text-white text-xs font-bold px-2 py-1 rounded-lg">
                    {review.rating_overall || 0}/5
                  </div>
                </div>
              )}
              <div className="flex-1">
                <h3 className="font-bold text-white text-xl mb-2">
                  {review.listing_title || 'N/A'}
                </h3>
                {review.listing_description && (
                  <p className="text-gray-400 mb-4 line-clamp-3">
                    {review.listing_description}
                  </p>
                )}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="bg-gray-700/50 rounded-lg p-3">
                    <p className="text-xs text-gray-500 mb-1">Price</p>
                    <p className="text-lg font-bold text-white">
                      {formatCurrency(review.price)}
                    </p>
                  </div>
                  <div className="bg-gray-700/50 rounded-lg p-3">
                    <p className="text-xs text-gray-500 mb-1">Order ID</p>
                    <p className="font-medium text-gray-300">#{review.order_id || 'N/A'}</p>
                  </div>
                  <div className="bg-gray-700/50 rounded-lg p-3">
                    <p className="text-xs text-gray-500 mb-1">Category</p>
                    <p className="font-medium text-gray-300 flex items-center">
                      <FiTag className="w-3 h-3 mr-1" />
                      Fashion
                    </p>
                  </div>
                  <div className="bg-gray-700/50 rounded-lg p-3">
                    <p className="text-xs text-gray-500 mb-1">Listing ID</p>
                    <p className="font-medium text-gray-300">#{review.listing_id}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Right Column - User Info & Actions */}
        <div className="space-y-6">
          {/* Buyer Information */}
          <div className="bg-gray-800 rounded-xl border border-gray-700 p-6">
            <div className="flex items-center space-x-2 mb-4">
              <div className="w-10 h-10 rounded-lg bg-blue-900/30 flex items-center justify-center">
                <FiUser className="w-5 h-5 text-blue-400" />
              </div>
              <div>
                <h2 className="font-bold text-white">Buyer Information</h2>
                <p className="text-sm text-gray-400">Customer who purchased</p>
              </div>
            </div>
            <div className="space-y-4">
              <div className="bg-gray-700/30 rounded-lg p-3">
                <p className="text-xs text-gray-500 mb-1">Name</p>
                <p className="font-medium text-white">
                  {review.buyer_first_name} {review.buyer_last_name}
                </p>
              </div>
              {review.buyer_email && (
                <div className="bg-gray-700/30 rounded-lg p-3">
                  <p className="text-xs text-gray-500 mb-1">Email</p>
                  <p className="font-medium text-white flex items-center">
                    <FiMail className="w-4 h-4 mr-2" />
                    {review.buyer_email}
                  </p>
                </div>
              )}
              <div className="bg-gray-700/30 rounded-lg p-3">
                <p className="text-xs text-gray-500 mb-1">User ID</p>
                <p className="font-medium text-gray-300">#{review.buyer_id}</p>
              </div>
            </div>
          </div>

          {/* Seller Information */}
          <div className="bg-gray-800 rounded-xl border border-gray-700 p-6">
            <div className="flex items-center space-x-2 mb-4">
              <div className="w-10 h-10 rounded-lg bg-green-900/30 flex items-center justify-center">
                <FiPackage className="w-5 h-5 text-green-400" />
              </div>
              <div>
                <h2 className="font-bold text-white">Seller Information</h2>
                <p className="text-sm text-gray-400">Store or individual seller</p>
              </div>
            </div>
            <div className="space-y-4">
              <div className="bg-gray-700/30 rounded-lg p-3">
                <p className="text-xs text-gray-500 mb-1">Store Name</p>
                <p className="font-medium text-white">{review.store_name || 'Individual Seller'}</p>
              </div>
              <div className="bg-gray-700/30 rounded-lg p-3">
                <p className="text-xs text-gray-500 mb-1">Seller Name</p>
                <p className="font-medium text-white">
                  {review.seller_first_name} {review.seller_last_name}
                </p>
              </div>
              {review.seller_email && (
                <div className="bg-gray-700/30 rounded-lg p-3">
                  <p className="text-xs text-gray-500 mb-1">Email</p>
                  <p className="font-medium text-white flex items-center">
                    <FiMail className="w-4 h-4 mr-2" />
                    {review.seller_email}
                  </p>
                </div>
              )}
              {review.store_description && (
                <div className="bg-gray-700/30 rounded-lg p-3">
                  <p className="text-xs text-gray-500 mb-1">Store Description</p>
                  <p className="text-sm text-gray-300 line-clamp-2">
                    {review.store_description}
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Order Information */}
          <div className="bg-gray-800 rounded-xl border border-gray-700 p-6">
            <div className="flex items-center space-x-2 mb-4">
              <div className="w-10 h-10 rounded-lg bg-purple-900/30 flex items-center justify-center">
                <FiShoppingBag className="w-5 h-5 text-purple-400" />
              </div>
              <div>
                <h2 className="font-bold text-white">Order Information</h2>
                <p className="text-sm text-gray-400">Transaction details</p>
              </div>
            </div>
            <div className="space-y-3">
              <div className="flex justify-between items-center p-2 hover:bg-gray-700/30 rounded-lg transition-colors">
                <span className="text-gray-400">Order Status:</span>
                <span className={`font-medium ${
                  review.order_status === 'completed' ? 'text-green-400' : 'text-yellow-400'
                }`}>
                  {review.order_status || 'N/A'}
                </span>
              </div>
              <div className="flex justify-between items-center p-2 hover:bg-gray-700/30 rounded-lg transition-colors">
                <span className="text-gray-400">Payment Status:</span>
                <span className={`font-medium ${
                  review.payment_status === 'paid' ? 'text-green-400' : 'text-red-400'
                }`}>
                  {review.payment_status || 'N/A'}
                </span>
              </div>
              <div className="flex justify-between items-center p-2 hover:bg-gray-700/30 rounded-lg transition-colors">
                <span className="text-gray-400">Order Date:</span>
                <span className="font-medium text-gray-300">
                  {formatDate(review.created_at)}
                </span>
              </div>
              {review.total_amount && (
                <div className="flex justify-between items-center p-2 hover:bg-gray-700/30 rounded-lg transition-colors">
                  <span className="text-gray-400">Order Total:</span>
                  <span className="font-bold text-white">
                    {formatCurrency(review.total_amount)}
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* Actions */}
          {review.status === 'pending' && (
            <div className="bg-gray-800 rounded-xl border border-gray-700 p-6">
              <h2 className="font-bold text-white mb-4">Moderation Actions</h2>
              <div className="space-y-3">
                <button
                  onClick={handleApprove}
                  disabled={actionLoading}
                  className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-green-900/30 hover:bg-green-900/50 text-green-400 border border-green-800/50 rounded-lg disabled:opacity-50 transition-all duration-200 font-medium hover:scale-[1.02]"
                >
                  <FiCheckCircle className="w-5 h-5" />
                  {actionLoading ? 'Processing...' : 'Approve Review'}
                </button>
                
                <button
                  onClick={() => setShowRejectModal(true)}
                  disabled={actionLoading}
                  className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-red-900/30 hover:bg-red-900/50 text-red-400 border border-red-800/50 rounded-lg disabled:opacity-50 transition-all duration-200 font-medium hover:scale-[1.02]"
                >
                  <FiXCircle className="w-5 h-5" />
                  Reject Review
                </button>
              </div>
            </div>
          )}

          {/* Review Metadata */}
          <div className="bg-gray-800 rounded-xl border border-gray-700 p-6">
            <h2 className="font-bold text-white mb-4">Review Metadata</h2>
            <div className="space-y-3">
              <div className="flex justify-between items-center p-2 hover:bg-gray-700/30 rounded-lg transition-colors">
                <span className="text-gray-400">Review ID:</span>
                <span className="font-medium text-gray-300">#{review.rating_id}</span>
              </div>
              <div className="flex justify-between items-center p-2 hover:bg-gray-700/30 rounded-lg transition-colors">
                <span className="text-gray-400">Created:</span>
                <span className="font-medium text-gray-300">{formatDate(review.created_at)}</span>
              </div>
              <div className="flex justify-between items-center p-2 hover:bg-gray-700/30 rounded-lg transition-colors">
                <span className="text-gray-400">Last Updated:</span>
                <span className="font-medium text-gray-300">{formatDate(review.updated_at)}</span>
              </div>
              {review.approved_at && (
                <div className="flex justify-between items-center p-2 hover:bg-gray-700/30 rounded-lg transition-colors">
                  <span className="text-gray-400">Approved:</span>
                  <span className="font-medium text-green-400">{formatDate(review.approved_at)}</span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Reject Modal */}
      {showRejectModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-800 rounded-xl border border-gray-700 w-full max-w-md animate-fade-in">
            <div className="p-6 border-b border-gray-700">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-medium text-white">Reject Review</h3>
                <button
                  onClick={() => {
                    setShowRejectModal(false);
                    setRejectionReason('Violation of review guidelines');
                  }}
                  className="text-gray-400 hover:text-white transition-colors"
                >
                  <FiX className="text-xl" />
                </button>
              </div>
              <p className="text-sm text-gray-400 mt-2">
                Are you sure you want to reject this review?
              </p>
            </div>
            
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Rejection Reason *
                </label>
                <select
                  value={rejectionReason}
                  onChange={(e) => setRejectionReason(e.target.value)}
                  className="w-full px-3 py-2.5 bg-gray-700 border border-gray-600 rounded-lg focus:ring-2 focus:ring-[#ff660e] focus:border-transparent text-white mb-2"
                >
                  <option value="Violation of review guidelines">Violation of review guidelines</option>
                  <option value="Contains offensive language">Contains offensive language</option>
                  <option value="Personal information included">Personal information included</option>
                  <option value="Not based on actual experience">Not based on actual experience</option>
                  <option value="Promotional content">Promotional content</option>
                  <option value="Other">Other (specify below)</option>
                </select>
                {rejectionReason === 'Other' && (
                  <textarea
                    value={rejectionReason}
                    onChange={(e) => setRejectionReason(e.target.value)}
                    placeholder="Specify rejection reason..."
                    rows="3"
                    className="w-full px-3 py-2.5 bg-gray-700 border border-gray-600 rounded-lg focus:ring-2 focus:ring-[#ff660e] focus:border-transparent text-white placeholder-gray-500"
                  />
                )}
                <p className="text-xs text-gray-500 mt-1">
                  This reason will be shown to the buyer.
                </p>
              </div>
              
              <div className="flex justify-end gap-3 pt-4 border-t border-gray-700">
                <button
                  onClick={() => {
                    setShowRejectModal(false);
                    setRejectionReason('Violation of review guidelines');
                  }}
                  className="px-4 py-2 text-gray-300 bg-gray-700 border border-gray-600 rounded-lg hover:bg-gray-600 transition-colors font-medium"
                >
                  Cancel
                </button>
                <button
                  onClick={handleReject}
                  disabled={!rejectionReason.trim() || actionLoading}
                  className="px-4 py-2 bg-gradient-to-r from-[#ff660e] to-orange-500 text-white rounded-lg hover:from-[#ff5500] hover:to-orange-600 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {actionLoading ? 'Processing...' : 'Reject Review'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* CSS for animations */}
      <style jsx>{`
        @keyframes fade-in {
          from { opacity: 0; transform: translateY(-10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-fade-in {
          animation: fade-in 0.3s ease-out;
        }
        
        .line-clamp-2 {
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }
        
        .line-clamp-3 {
          display: -webkit-box;
          -webkit-line-clamp: 3;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }
      `}</style>
    </div>
  );
}

export default ReviewDetail;